import React, { useState, useEffect, useMemo, useCallback } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import achalLogo from "../../assets/images/achal_logo.png";
import {
  FiSearch, FiActivity, FiMapPin, FiImage, FiPlayCircle,
  FiMic, FiX, FiPhone, FiMessageCircle,
  FiClipboard, FiFileText, FiRefreshCw, FiExternalLink,
  FiClock, FiCheckCircle, FiTag,
  FiFilter, FiCalendar, FiShield, FiInfo, FiEye,
  FiUser, FiStar, FiMessageSquare, FiLayers, FiGrid,
  FiPauseCircle, FiXCircle, FiChevronDown,
  FiHash, FiChevronRight, FiDownload, FiBarChart2
} from "react-icons/fi";

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const MEDIA_BASE_URL   = "https://api.ajplservices.tech/uploads/tickets/";
const PROFILE_BASE_URL = "https://api.ajplservices.tech/uploads/profile/";

const ACHAL_LOGO    = achalLogo;

const STATUS_TABS = ["All", "Pending", "Work in Progress", "Resolved", "Rejected", "Hold"];

const STATUS_META = {
  pending:              { bg: "#FFFBEB", txt: "#D97706", border: "#FDE68A", icon: <FiClock size={11}/> },
  resolved:             { bg: "#ECFDF5", txt: "#059669", border: "#A7F3D0", icon: <FiCheckCircle size={11}/> },
  rejected:             { bg: "#FEF2F2", txt: "#DC2626", border: "#FECACA", icon: <FiXCircle size={11}/> },
  "work in progress":   { bg: "#EFF6FF", txt: "#2563EB", border: "#BFDBFE", icon: <FiActivity size={11}/> },
  hold:                 { bg: "#F5F3FF", txt: "#7C3AED", border: "#DDD6FE", icon: <FiPauseCircle size={11}/> },
};

const RATING_STARS_UI = (n) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} style={{ color: i < n ? "#F59E0B" : "#E2E8F0", fontSize: "14px" }}>★</span>
  ));

/* ─────────────────────────────────────────
   HELPERS — Time Calculation
───────────────────────────────────────── */
const calcTimeTaken = (createdAt, resolvedAt) => {
  if (!resolvedAt || !createdAt) return null;
  const diff = new Date(resolvedAt) - new Date(createdAt);
  if (isNaN(diff) || diff < 0) return null;
  return Math.round(diff / 60000); // ms → minutes
};

const formatResolvedAt = (resolvedAt) => {
  if (!resolvedAt) return null;
  const d = new Date(resolvedAt);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/* ─────────────────────────────────────────
   SCANNED LOCATION BADGE
───────────────────────────────────────── */
const ScannedLocBadge = ({ location }) => {
  if (!location) return null;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      background: "#FEF2F2", color: "#DC2626",
      border: "1.5px solid #FECACA", borderRadius: "30px",
      padding: "4px 10px", fontSize: "11px", fontWeight: 800,
      marginTop: "5px", width: "fit-content", whiteSpace: "nowrap",
    }}>
      <FiMapPin size={11}/> {location}
    </div>
  );
};

/* ─────────────────────────────────────────
   DRAWER VERIFIED LOCATION BOX
───────────────────────────────────────── */
const VerifiedLocationBox = ({ location }) => {
  if (!location) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "14px",
      background: "#FEF2F2", border: "1.5px solid #FECACA",
      borderRadius: "14px", padding: "14px 16px", marginBottom: "12px",
    }}>
      <div style={{
        width: "42px", height: "42px", borderRadius: "50%",
        background: "#DC2626", display: "flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0,
        boxShadow: "0 4px 12px rgba(220,38,38,0.3)",
      }}>
        <FiMapPin size={18} color="#FFF"/>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: "9px", fontWeight: 900, color: "#EF4444",
          textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "4px",
        }}>
          ✓ Verified Scanned Location
        </div>
        <div style={{
          fontSize: "15px", fontWeight: 800, color: "#7F1D1D", lineHeight: "1.3",
        }}>
          {location}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   SINGLE TICKET PDF DOWNLOAD — unchanged
───────────────────────────────────────── */
const downloadTicketPDF = (ticket) => {
  const statusColor = {
    resolved: "#059669", pending: "#D97706", rejected: "#DC2626",
    "work in progress": "#2563EB", hold: "#7C3AED",
  };
  const sColor = statusColor[(ticket.status || "").toLowerCase()] || "#475569";

  const achalLogoUrl    = ACHAL_LOGO;

  const scannedLocationHTML = ticket.scanned_location ? `
    <div class="section">
      <div class="section-title">Verified Scanned Location</div>
      <div class="section-card" style="background:#FEF2F2;border:1.5px solid #FECACA;border-radius:10px;padding:10px 14px;display:flex;align-items:center;gap:12px;">
        <div style="width:34px;height:34px;border-radius:50%;background:#DC2626;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <span style="color:#FFF;font-size:16px;">⊙</span>
        </div>
        <div>
          <div style="font-size:9px;font-weight:900;color:#EF4444;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:3px;">✓ VERIFIED SCANNED LOCATION</div>
          <div style="font-size:14px;font-weight:800;color:#7F1D1D;">${ticket.scanned_location}</div>
        </div>
      </div>
    </div>
  ` : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Ticket ${ticket.ticket_code || ticket.id}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,600;0,700;0,800;0,900;1,400&family=DM+Mono:wght@500;600&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    html,body { width:210mm; height:297mm; font-family:'DM Sans',sans-serif; color:#1E293B; background:#fff; }
    .page { width:210mm; height:297mm; overflow:hidden; display:flex; flex-direction:column; background:#fff; }
    .header { background:linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%); padding:18px 32px 14px 28px; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; position:relative; overflow:hidden; box-sizing:border-box; }
    .header::after { content:''; position:absolute; top:-20px; right:-20px; width:120px; height:120px; border-radius:50%; background:rgba(212,175,55,0.08); }
    .header-left { display:flex; align-items:center; gap:14px; flex-shrink:0; }
    .achal-logo { width:52px; height:52px; object-fit:contain; border-radius:12px; background:#D4AF37; padding:4px; }
    .brand-name { font-size:20px; font-weight:900; color:#D4AF37; letter-spacing:2px; text-transform:uppercase; line-height:1; }
    .brand-tagline { font-size:9px; color:rgba(255,255,255,0.6); letter-spacing:1.5px; text-transform:uppercase; margin-top:3px; }
    .header-right { text-align:right; flex-shrink:0; max-width:200px; padding-right:4px; box-sizing:border-box; z-index:1; }
    .ticket-ref-label { font-size:9px; color:rgba(255,255,255,0.65); letter-spacing:1.5px; text-transform:uppercase; white-space:nowrap; }
    .ticket-ref-val { font-family:'DM Mono',monospace; font-size:18px; font-weight:600; color:#D4AF37; letter-spacing:2px; word-break:break-all; }
    .ticket-date-val { font-size:10px; color:rgba(255,255,255,0.65); margin-top:3px; white-space:nowrap; }
    .gold-strip { background:linear-gradient(90deg,#D4AF37,#F0CC5F,#D4AF37); height:4px; flex-shrink:0; }
    .status-row { padding:10px 28px; background:#F8FAFC; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid #E2E8F0; flex-shrink:0; }
    .status-pill { display:inline-flex; align-items:center; gap:6px; padding:6px 18px; border-radius:30px; font-size:11px; font-weight:900; letter-spacing:0.8px; text-transform:uppercase; border:1.5px solid; }
    .status-dot { width:7px; height:7px; border-radius:50%; }
    .id-mono { font-family:'DM Mono',monospace; font-size:11px; font-weight:600; color:#475569; }
    .body { flex:1; padding:16px 28px 0; display:flex; flex-direction:column; gap:10px; overflow:hidden; }
    .section { }
    .section-title { font-size:8px; font-weight:900; color:#475569; letter-spacing:2px; text-transform:uppercase; margin-bottom:6px; display:flex; align-items:center; gap:6px; }
    .section-title::after { content:''; flex:1; height:1px; background:#E2E8F0; }
    .section-card { background:#FAFBFC; border:1px solid #E8EDF5; border-radius:10px; padding:10px 14px; }
    .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:6px 20px; }
    .grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:6px 16px; }
    .info-item { }
    .info-label { font-size:9px; font-weight:800; color:#475569; text-transform:uppercase; letter-spacing:0.8px; }
    .info-val { font-size:12px; font-weight:800; color:#1E293B; margin-top:2px; line-height:1.3; }
    .info-val.mono { font-family:'DM Mono',monospace; }
    .desc-box { background:#F1F5F9; border-left:3px solid #D4AF37; border-radius:0 8px 8px 0; padding:8px 12px; font-size:11px; color:#334155; line-height:1.55; font-style:italic; }
    .remarks-box { background:#FFF7ED; border-left:3px solid #F59E0B; border-radius:0 8px 8px 0; padding:8px 12px; font-size:11px; color:#78350F; line-height:1.55; font-style:italic; }
    .rating-row { display:flex; align-items:center; gap:10px; }
    .stars { font-size:20px; letter-spacing:3px; line-height:1; }
    .stars-filled { color:#F59E0B; }
    .stars-empty { color:#D1D5DB; }
    .rating-num { font-size:13px; font-weight:900; color:#F59E0B; }
    .feedback-text { font-size:11px; color:#334155; font-style:italic; margin-top:8px; padding:7px 10px; background:#FFFBEB; border-radius:6px; line-height:1.5; }
    .no-rating { font-size:11px; color:#64748B; font-style:italic; }
    .tear { display:flex; align-items:center; margin:0 -28px; flex-shrink:0; }
    .tear-circle { width:12px; height:12px; border-radius:50%; background:#E2E8F0; flex-shrink:0; }
    .tear-line { flex:1; border-top:1.5px dashed #CBD5E1; }
    .footer { padding:10px 28px 14px; background:#F8FAFC; border-top:1px solid #E2E8F0; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
    .powered-by-label { font-size:8px; color:#475569; font-weight:700; letter-spacing:1px; text-transform:uppercase; margin-bottom:4px; }
    .tristara-logo { height:28px; object-fit:contain; }
    .tristara-fallback { font-size:13px; font-weight:900; color:#1E293B; }
    .tristara-fallback span { color:#D4AF37; }
    .footer-right { text-align:right; }
    .footer-note { font-size:9px; color:#334155; font-weight:600; }
    .footer-ids { font-family:'DM Mono',monospace; font-size:9px; color:#475569; margin-top:3px; }
    @media print { html,body { width:210mm; height:297mm; } .page { width:210mm; height:297mm; } @page { margin:0; size:A4 portrait; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="header-left">
      <img src="${achalLogoUrl}" class="achal-logo" alt="Achal Jewels" onerror="this.style.display='none';"/>
      <div>
        <div class="brand-name">Achal Jewels</div>
        <div class="brand-tagline">Support Ticket — Official Record</div>
      </div>
    </div>
    <div class="header-right">
      <div class="ticket-ref-label">Ticket Reference</div>
      <div class="ticket-ref-val">${ticket.ticket_code || `#${ticket.id}`}</div>
      <div class="ticket-date-val">${new Date(ticket.created_at).toLocaleString("en-IN", { day:"2-digit", month:"long", year:"numeric", hour:"2-digit", minute:"2-digit" })}</div>
      <div style="margin-top:4px;font-size:9px;color:rgba(255,255,255,0.55);letter-spacing:1px;text-transform:uppercase;">Ticket ID &middot; ${ticket.id}</div>
    </div>
  </div>
  <div class="gold-strip"></div>
  <div class="status-row">
    <div class="status-pill" style="color:${sColor};border-color:${sColor};background:${sColor}18;">
      <div class="status-dot" style="background:${sColor};"></div>
      ${(ticket.status || "Pending").toUpperCase()}
    </div>
    <div class="id-mono">Ref: ${ticket.ticket_code || `#${ticket.id}`}</div>
  </div>
  <div class="body">
    <div class="section">
      <div class="section-title">Requestor Details</div>
      <div class="section-card">
        <div class="grid-3">
          <div class="info-item"><div class="info-label">Employee Name</div><div class="info-val">${ticket.raised_by || "—"}</div></div>
          <div class="info-item"><div class="info-label">Phone</div><div class="info-val mono">${ticket.user_phone || "—"}</div></div>
          <div class="info-item"><div class="info-label">Location</div><div class="info-val">${(ticket.floor_number || ticket.cabin_number) ? `Floor ${ticket.floor_number || "—"} · Cabin ${ticket.cabin_number || "—"}` : "—"}</div></div>
        </div>
      </div>
    </div>
    ${scannedLocationHTML}
    <div class="section">
      <div class="section-title">Ticket Classification</div>
      <div class="section-card">
        <div class="grid-2" style="margin-bottom:${ticket.description ? "10px" : "0"};">
          <div class="info-item"><div class="info-label">Main Category</div><div class="info-val">${ticket.category_name || "—"}</div></div>
          <div class="info-item"><div class="info-label">Sub-Category</div><div class="info-val">${ticket.sub_category_name || "—"}</div></div>
        </div>
        ${ticket.description ? `<div class="info-label" style="margin-bottom:5px;">Description</div><div class="desc-box">${ticket.description}</div>` : ""}
      </div>
    </div>
    <div class="section">
      <div class="section-title">Assignment &amp; Resolution</div>
      <div class="section-card">
        <div class="grid-2" style="margin-bottom:${ticket.remarks ? "10px" : "0"};">
          <div class="info-item"><div class="info-label">Assigned Worker</div><div class="info-val">${ticket.assigned_worker_name || "Unassigned"}</div></div>
          <div class="info-item"><div class="info-label">Final Status</div><div class="info-val" style="color:${sColor};">${ticket.status || "—"}</div></div>
        </div>
        ${ticket.remarks ? `<div class="info-label" style="margin-bottom:5px;">HOD Remarks</div><div class="remarks-box">${ticket.remarks}</div>` : ""}
      </div>
    </div>
    <div class="tear"><div class="tear-circle"></div><div class="tear-line"></div><div class="tear-circle"></div></div>
    <div class="section">
      <div class="section-title">User Satisfaction Rating</div>
      <div class="section-card" style="padding:10px 14px;">
        ${ticket.rating > 0 ? `
        <div class="rating-row">
          <span class="stars"><span class="stars-filled">${"★".repeat(Math.min(parseInt(ticket.rating)||0,5))}</span><span class="stars-empty">${"☆".repeat(5-Math.min(parseInt(ticket.rating)||0,5))}</span></span>
          <span class="rating-num">${ticket.rating} / 5</span>
        </div>
        ${ticket.feedback ? `<div class="feedback-text">"${ticket.feedback}"</div>` : ""}
        ` : `<div class="no-rating">No rating submitted yet by the user.</div>`}
      </div>
    </div>
  </div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},600);};</script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=820,height=1120");
  if (win) { win.document.write(html); win.document.close(); }
};

/* ─────────────────────────────────────────
   CSV EXPORT — NEW
   Filters by created_at, grouped by category,
   sorted by created_at within each category
───────────────────────────────────────── */
const downloadMonthlyReportCSV = (filteredData, filterDateFrom, filterDateTo) => {
  if (!filteredData || filteredData.length === 0) {
    alert("No tickets found in the current filter range to export CSV.");
    return;
  }

  // Group by category, sort each group by created_at
  const grouped = {};
  filteredData.forEach(t => {
    const cat = t.category_name || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });
  Object.keys(grouped).forEach(cat => {
    grouped[cat].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const sortedCategories = Object.keys(grouped).sort();

  const escapeCSV = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str}"` : str;
  };

  const formatDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const headers = [
    "Ticket Code",
    "Employee",
    "Category",
    "Subcategory",
    "Status",
    "Ticket Raised On",
    "Ticket Resolved On",
    "Time Taken (min)",
    "Rating",
    "Feedback",
    "Assigned Worker",
    "Remarks",
  ];

  const rows = [];
  // Report metadata rows
  const dateRangeLabel = (filterDateFrom && filterDateTo)
    ? `${new Date(filterDateFrom).toLocaleDateString("en-IN")} to ${new Date(filterDateTo).toLocaleDateString("en-IN")}`
    : filterDateFrom
      ? `From ${new Date(filterDateFrom).toLocaleDateString("en-IN")}`
      : "All Dates";

  rows.push([`"Achal Jewels — Monthly Ticket Report"`]);
  rows.push([`"Date Range: ${dateRangeLabel}"`]);
  rows.push([`"Generated: ${new Date().toLocaleString("en-IN")}"`]);
  rows.push([`"Total Tickets: ${filteredData.length}"`]);
  rows.push([""]);
  rows.push(headers.map(escapeCSV));

  sortedCategories.forEach(cat => {
    // Category header row
    rows.push([`"── ${cat} (${grouped[cat].length} ticket${grouped[cat].length !== 1 ? "s" : ""}) ──"`]);

    grouped[cat].forEach(t => {
      const timeTaken = calcTimeTaken(t.created_at, t.resolved_at);
      rows.push([
        escapeCSV(t.ticket_code || `#${t.id}`),
        escapeCSV(t.raised_by || ""),
        escapeCSV(t.category_name || ""),
        escapeCSV(t.sub_category_name || ""),
        escapeCSV(t.status || ""),
        escapeCSV(formatDate(t.created_at)),
        escapeCSV(formatDate(t.resolved_at)),
        escapeCSV(timeTaken !== null ? timeTaken : ""),
        escapeCSV(t.rating > 0 ? t.rating : ""),
        escapeCSV(t.feedback || ""),
        escapeCSV(t.assigned_worker_name || ""),
        escapeCSV(t.remarks || ""),
      ]);
    });

    rows.push([""]);
  });

  const csvContent = rows.map(r => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `Achal_Monthly_Report_${filterDateFrom || "all"}_to_${filterDateTo || "all"}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ─────────────────────────────────────────
   MONTHLY REPORT PDF — FIXED
   • Filters by ticket.created_at   ← CORRECT
   • resolved_at used ONLY for display columns
   • Proper landscape A4, no overflow
   • All columns fit, compact font
   • Category grouping + date sort
───────────────────────────────────────── */
const downloadMonthlyReportPDF = (filteredData, filterDateFrom, filterDateTo) => {
  if (!filteredData || filteredData.length === 0) {
    alert("No tickets found in the current filter range to generate a report.");
    return;
  }

  // Group by category, sort each group by created_at
  const grouped = {};
  filteredData.forEach(t => {
    const cat = t.category_name || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });
  Object.keys(grouped).forEach(cat => {
    grouped[cat].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const sortedCategories = Object.keys(grouped).sort();

  const statusColor = {
    resolved:           "#059669",
    pending:            "#D97706",
    rejected:           "#DC2626",
    "work in progress": "#2563EB",
    hold:               "#7C3AED",
  };

  const dateRangeLabel = (filterDateFrom && filterDateTo)
    ? `${new Date(filterDateFrom).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" })} — ${new Date(filterDateTo).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" })}`
    : filterDateFrom
      ? `From ${new Date(filterDateFrom).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" })}`
      : "All Dates";

  const totalResolved = filteredData.filter(t => (t.status||"").toLowerCase() === "resolved").length;
  const totalPending  = filteredData.filter(t => (t.status||"").toLowerCase() === "pending").length;
  const totalWIP      = filteredData.filter(t => (t.status||"").toLowerCase() === "work in progress").length;
  const resolvedPercent = filteredData.length ? ((totalResolved / filteredData.length) * 100).toFixed(2) : "0.00";
  const pendingPercent  = filteredData.length ? ((totalPending  / filteredData.length) * 100).toFixed(2) : "0.00";

  const esc = (v) => String(v || "—").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const buildRows = (tickets) => tickets.map((t, idx) => {
    const sColor     = statusColor[(t.status||"").toLowerCase()] || "#475569";
    const raisedOn   = new Date(t.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
    const raisedTime = new Date(t.created_at).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });
    // resolved_at used ONLY for display
    const resolvedOn = t.resolved_at
      ? new Date(t.resolved_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) +
        "<br><span style='font-size:8px;color:#6EE7B7;'>" +
        new Date(t.resolved_at).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" }) + "</span>"
      : "<span style='color:#CBD5E1;'>—</span>";
    const timeTaken  = calcTimeTaken(t.created_at, t.resolved_at);
    const timeTakenStr = timeTaken !== null
      ? `<span style="color:#7C3AED;font-weight:800;">${timeTaken}<span style="font-size:8px;font-weight:600;"> min</span></span>`
      : "<span style='color:#CBD5E1;'>—</span>";
    const stars = t.rating > 0
      ? `<span style="color:#F59E0B;font-size:11px;">${"★".repeat(Math.min(parseInt(t.rating)||0,5))}</span><span style="color:#E2E8F0;font-size:11px;">${"☆".repeat(5-Math.min(parseInt(t.rating)||0,5))}</span>`
      : "<span style='color:#CBD5E1;font-size:10px;'>—</span>";
    const feedback = t.feedback
      ? `<span style="font-style:italic;color:#475569;font-size:9px;">${esc(t.feedback)}</span>`
      : "<span style='color:#CBD5E1;'>—</span>";
    const bg = idx % 2 === 0 ? "#FFFFFF" : "#F8FAFC";

    return `<tr style="background:${bg};page-break-inside:avoid;">
      <td style="padding:7px 8px;font-family:'DM Mono',monospace;font-size:9px;font-weight:700;color:#9A7B10;background:#D4AF3712;white-space:nowrap;border-bottom:1px solid #F1F5F9;">${esc(t.ticket_code || `#${t.id}`)}</td>
      <td style="padding:7px 8px;font-size:9px;font-weight:700;color:#1E293B;border-bottom:1px solid #F1F5F9;white-space:nowrap;">${esc(t.raised_by)}</td>
      <td style="padding:7px 8px;font-size:9px;color:#334155;border-bottom:1px solid #F1F5F9;">${esc(t.sub_category_name)}</td>
      <td style="padding:7px 8px;font-size:9px;color:#334155;border-bottom:1px solid #F1F5F9;white-space:nowrap;">${raisedOn}<br><span style="font-size:8px;color:#94A3B8;">${raisedTime}</span></td>
      <td style="padding:7px 8px;border-bottom:1px solid #F1F5F9;white-space:nowrap;"><span style="display:inline-block;padding:3px 7px;border-radius:20px;font-size:8px;font-weight:800;color:${sColor};background:${sColor}18;border:1px solid ${sColor}40;text-transform:uppercase;letter-spacing:0.3px;">${esc(t.status||"Pending")}</span></td>
      <td style="padding:7px 8px;font-size:9px;font-weight:700;color:#2563EB;border-bottom:1px solid #F1F5F9;white-space:nowrap;">${esc(t.assigned_worker_name||"Unassigned")}</td>
      <td style="padding:7px 8px;font-size:9px;font-weight:700;color:#059669;border-bottom:1px solid #F1F5F9;">${resolvedOn}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #F1F5F9;text-align:center;">${timeTakenStr}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #F1F5F9;white-space:nowrap;">${stars}</td>
      <td style="padding:7px 8px;border-bottom:1px solid #F1F5F9;max-width:140px;word-wrap:break-word;word-break:break-word;white-space:normal;line-height:1.5;">${feedback}</td>
    </tr>`;
  }).join("");

  const buildCategorySection = (catName, tickets) => `
    <div style="margin-bottom:22px;page-break-inside:avoid;">
      <!-- Category Header -->
      <div style="background:linear-gradient(90deg,#0F172A,#1E293B);padding:10px 16px;border-radius:10px 10px 0 0;display:flex;align-items:center;gap:12px;page-break-after:avoid;">
        <span style="color:#D4AF37;font-size:16px;font-weight:900;">◈</span>
        <div style="flex:1;">
          <div style="font-size:13px;font-weight:900;color:#FFF;letter-spacing:0.3px;">${esc(catName)}</div>
          <div style="font-size:9px;color:rgba(255,255,255,0.5);margin-top:2px;">${tickets.length} ticket${tickets.length !== 1 ? "s" : ""}</div>
        </div>
        <div style="display:flex;gap:8px;">
          <span style="font-size:9px;font-weight:800;padding:3px 10px;border-radius:20px;background:rgba(5,150,105,0.25);color:#6EE7B7;">${tickets.filter(t=>(t.status||"").toLowerCase()==="resolved").length} Resolved</span>
          <span style="font-size:9px;font-weight:800;padding:3px 10px;border-radius:20px;background:rgba(217,119,6,0.25);color:#FCD34D;">${tickets.filter(t=>(t.status||"").toLowerCase()==="pending").length} Pending</span>
          <span style="font-size:9px;font-weight:800;padding:3px 10px;border-radius:20px;background:rgba(37,99,235,0.2);color:#93C5FD;">${tickets.filter(t=>(t.status||"").toLowerCase()==="work in progress").length} WIP</span>
        </div>
      </div>
      <!-- Table -->
      <table style="width:100%;border-collapse:collapse;border:1.5px solid #E2E8F0;border-top:none;border-radius:0 0 10px 10px;table-layout:fixed;">
        <colgroup>
          <col style="width:9%"/>  <!-- Ticket Code -->
          <col style="width:10%"/> <!-- Employee -->
          <col style="width:10%"/> <!-- Sub-Cat -->
          <col style="width:9%"/>  <!-- Raised On -->
          <col style="width:9%"/>  <!-- Status -->
          <col style="width:10%"/> <!-- Worker -->
          <col style="width:10%"/> <!-- Resolved On -->
          <col style="width:7%"/>  <!-- Time Taken -->
          <col style="width:8%"/>  <!-- Rating -->
          <col style="width:18%"/> <!-- Feedback -->
        </colgroup>
        <thead>
          <tr style="page-break-after:avoid;">
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;white-space:nowrap;">TICKET</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;white-space:nowrap;">EMPLOYEE</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;">SUB-CATEGORY</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;white-space:nowrap;">RAISED ON</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;">STATUS</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#64748B;text-transform:uppercase;letter-spacing:0.8px;background:#F1F5F9;border-bottom:1.5px solid #E2E8F0;">WORKER</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#2563EB;text-transform:uppercase;letter-spacing:0.8px;background:#EFF6FF;border-bottom:1.5px solid #E2E8F0;white-space:nowrap;">RESOLVED ON</th>
            <th style="padding:8px 8px;text-align:center;font-size:8px;font-weight:900;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;background:#F5F3FF;border-bottom:1.5px solid #E2E8F0;white-space:nowrap;">TIME (MIN)</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#D97706;text-transform:uppercase;letter-spacing:0.8px;background:#FFFBEB;border-bottom:1.5px solid #E2E8F0;">RATING</th>
            <th style="padding:8px 8px;text-align:left;font-size:8px;font-weight:900;color:#059669;text-transform:uppercase;letter-spacing:0.8px;background:#ECFDF5;border-bottom:1.5px solid #E2E8F0;">FEEDBACK</th>
          </tr>
        </thead>
        <tbody>
          ${buildRows(tickets)}
        </tbody>
      </table>
    </div>
  `;

  const categorySections = sortedCategories.map(cat =>
    buildCategorySection(cat, grouped[cat])
  ).join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Achal Jewels — Monthly Ticket Report</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    html, body {
      font-family: 'DM Sans', sans-serif;
      color: #1E293B;
      background: #fff;
      font-size: 11px;
      /* No horizontal overflow */
      overflow-x: hidden;
    }

    /* ── REPORT HEADER ── */
    .report-header {
      background: linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #243451 100%);
      padding: 20px 28px 16px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      position: relative;
      overflow: hidden;
    }
    .report-header::before {
      content: '';
      position: absolute;
      top: -30px; right: -30px;
      width: 160px; height: 160px;
      border-radius: 50%;
      background: rgba(212,175,55,0.06);
    }
    .brand-block { display:flex; align-items:center; gap:14px; z-index:1; }
    .brand-logo { width:46px; height:46px; object-fit:contain; background:#D4AF37; border-radius:12px; padding:4px; }
    .brand-logo-fallback { width:46px;height:46px;border-radius:12px;background:#D4AF37;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:900;color:#0F172A; }
    .brand-name { font-size:19px; font-weight:900; color:#D4AF37; letter-spacing:2px; text-transform:uppercase; line-height:1; }
    .brand-sub  { font-size:8px; color:rgba(255,255,255,0.5); letter-spacing:2px; text-transform:uppercase; margin-top:4px; }
    .report-meta { text-align:right; z-index:1; }
    .report-title    { font-size:14px; font-weight:900; color:#FFF; }
    .report-subtitle { font-size:9px; color:rgba(255,255,255,0.55); margin-top:2px; letter-spacing:0.5px; }
    .report-range    { font-size:10px; color:#D4AF37; margin-top:5px; font-weight:700; }
    .report-generated{ font-size:8px; color:rgba(255,255,255,0.45); margin-top:3px; }

    .gold-strip { background:linear-gradient(90deg,#D4AF37,#F0CC5F,#D4AF37); height:3px; }

    /* ── SUMMARY STRIP ── */
    .summary-strip {
      display: flex;
      background: #F8FAFC;
      border-bottom: 2px solid #E2E8F0;
      padding: 0 28px;
    }
    .summary-item {
      flex: 1;
      padding: 12px 10px;
      border-right: 1px solid #E8EDF5;
      text-align: center;
    }
    .summary-item:last-child { border-right: none; }
    .sum-label { font-size:7px; font-weight:800; color:#94A3B8; text-transform:uppercase; letter-spacing:1px; }
    .sum-val { font-size:20px; font-weight:900; color:#0F172A; margin-top:2px; line-height:1; }
    .sum-val.gold  { color:#D4AF37; }
    .sum-val.green { color:#059669; }
    .sum-val.amber { color:#D97706; }
    .sum-val.blue  { color:#2563EB; }
    .sum-pct { font-size:10px; font-weight:700; margin-top:2px; }
    .green-pct { color:#059669; }
    .amber-pct { color:#D97706; }

    /* ── BODY ── */
    .body { padding: 18px 28px 36px; }

    /* ── FOOTER ── */
    .report-footer {
      border-top: 2px solid #E2E8F0;
      padding: 12px 28px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #F8FAFC;
    }
    .footer-brand { display:flex; align-items:center; gap:10px; }
    .powered-label { font-size:7px; font-weight:800; color:#94A3B8; text-transform:uppercase; letter-spacing:1px; margin-bottom:3px; }
    .tristara-logo { height:22px; object-fit:contain; }
    .tristara-fallback { font-size:11px; font-weight:900; color:#1E293B; }
    .tristara-fallback span { color:#D4AF37; }
    .footer-right { text-align:right; }
    .footer-note { font-size:8px; color:#334155; font-weight:700; }
    .footer-sub  { font-family:'DM Mono',monospace; font-size:7px; color:#94A3B8; margin-top:3px; }

    /* ── PRINT RULES ── */
    @media print {
      html, body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        overflow: hidden;
      }
      @page {
        size: A4 landscape;
        margin: 8mm 10mm;
      }
      .report-footer {
        position: running(footer);
      }
      /* Repeat table headers on every page */
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr    { page-break-inside: avoid; }
    }
  </style>
</head>
<body>

  <!-- PAGE HEADER -->
  <div class="report-header">
    <div class="brand-block">
      <img src="${ACHAL_LOGO}" class="brand-logo" alt="Achal Jewels"
        onerror="this.style.display='none';document.getElementById('logo-fb').style.display='flex';"/>
      <div id="logo-fb" class="brand-logo-fallback" style="display:none;">AJ</div>
      <div>
        <div class="brand-name">Achal Jewels</div>
        <div class="brand-sub">Support Management System</div>
      </div>
    </div>
    <div class="report-meta">
      <div class="report-title">Monthly Progress Report</div>
      <div class="report-subtitle">Categorywise · Sorted by Ticket Raised Date</div>
      <div class="report-range">Date Range: ${dateRangeLabel}</div>
      <div class="report-generated">Generated: ${new Date().toLocaleString("en-IN", { day:"2-digit", month:"long", year:"numeric", hour:"2-digit", minute:"2-digit" })}</div>
    </div>
  </div>

  <div class="gold-strip"></div>

  <!-- SUMMARY STRIP -->
  <div class="summary-strip">
    <div class="summary-item">
      <div class="sum-label">Total Tickets</div>
      <div class="sum-val">${filteredData.length}</div>
    </div>
    <div class="summary-item">
      <div class="sum-label">Resolved</div>
      <div class="sum-val green">${totalResolved}</div>
      <div class="sum-pct green-pct">${resolvedPercent}%</div>
    </div>
    <div class="summary-item">
      <div class="sum-label">Pending</div>
      <div class="sum-val amber">${totalPending}</div>
      <div class="sum-pct amber-pct">${pendingPercent}%</div>
    </div>
    <div class="summary-item">
      <div class="sum-label">In Progress</div>
      <div class="sum-val blue">${totalWIP}</div>
    </div>
    <div class="summary-item">
      <div class="sum-label">Categories</div>
      <div class="sum-val">${sortedCategories.length}</div>
    </div>
  </div>

  <!-- CATEGORY SECTIONS -->
  <div class="body">
    ${categorySections}
  </div>

  <!-- FOOTER -->
  <div class="report-footer">
    <div class="footer-right">
      <div class="footer-note">Achal Jewels — Confidential Internal Report</div>
      <div class="footer-sub">Total: ${filteredData.length} tickets · ${sortedCategories.length} categories · Report filtered by: Ticket Raised Date (created_at)</div>
    </div>
  </div>

<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 800); };</script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=1200,height=900");
  if (win) { win.document.write(html); win.document.close(); }
};

/* ─────────────────────────────────────────
   SMALL UI ATOMS — unchanged
───────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const key = (status || "pending").toLowerCase();
  const c   = STATUS_META[key] || STATUS_META.pending;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "6px 12px", borderRadius: "30px", fontSize: "11px",
      fontWeight: 800, background: c.bg, color: c.txt,
      border: `1.5px solid ${c.border}`, whiteSpace: "nowrap",
    }}>
      {c.icon} {status || "Pending"}
    </div>
  );
};

const Avatar = ({ src, name, size = 44 }) => {
  const [err, setErr] = useState(false);
  const initials = (name || "?").charAt(0).toUpperCase();
  const hue = ((name || "A").charCodeAt(0) * 37) % 360;
  if (src && !err) {
    return (
      <img
        src={`${PROFILE_BASE_URL}${src}`}
        alt={name}
        onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: "14px", objectFit: "cover", border: "2px solid #F1F5F9", flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "14px", flexShrink: 0,
      background: `hsl(${hue},58%,52%)`, display: "flex", alignItems: "center",
      justifyContent: "center", color: "#FFF", fontWeight: 900, fontSize: size * 0.38,
    }}>
      {initials}
    </div>
  );
};

const Section = ({ title, children, icon }) => (
  <div style={{ marginBottom: "22px" }}>
    <div style={S.secLabel}>
      {icon && <span style={{ marginRight: "5px", opacity: 0.7 }}>{icon}</span>}
      {title}
    </div>
    <div style={S.secCard}>{children}</div>
  </div>
);

const DataRow = ({ label, value, icon, isDesc, mono }) => (
  <div style={{
    display: "flex", marginBottom: isDesc ? "12px" : "9px",
    fontSize: "13px", flexDirection: isDesc ? "column" : "row", alignItems: "flex-start",
  }}>
    <span style={{
      minWidth: "130px", color: "#94A3B8", fontWeight: 700,
      display: "flex", alignItems: "center", gap: "6px",
      marginBottom: isDesc ? "6px" : 0, paddingTop: "1px", fontSize: "12px",
    }}>
      {icon} {label}
    </span>
    <span style={{
      fontWeight: 700, color: "#1E293B", flex: 1,
      fontSize: isDesc ? "13.5px" : "13px", lineHeight: isDesc ? "1.7" : "1.4",
      fontFamily: mono ? "'DM Mono', monospace" : "inherit",
    }}>
      {value || "—"}
    </span>
  </div>
);

const MediaPill = ({ type, icon, url }) => (
  <div onClick={() => window.open(url, "_blank")} style={S.mediaPill} title={`Open ${type}`}>
    {icon}
    <span style={{ fontWeight: 700, fontSize: "12px" }}>{type}</span>
  </div>
);

const StatCard = ({ label, value, color = "#1E293B", sub }) => (
  <div style={S.statCard}>
    <span style={S.statCardLabel}>{label}</span>
    <span style={{ ...S.statCardVal, color }}>{value}</span>
    {sub && <span style={S.statCardSub}>{sub}</span>}
  </div>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const AdminTickets = () => {
  const [tickets,        setTickets]        = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [searchTerm,     setSearchTerm]     = useState("");
  const [activeTab,      setActiveTab]      = useState("All");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCat,   setFilterSubCat]   = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo,   setFilterDateTo]   = useState("");
  const [filterRating,   setFilterRating]   = useState("");
  const [showFilters,    setShowFilters]    = useState(false);
  const [viewMode,       setViewMode]       = useState("table");

  // 👑 Admin Override Status states (Shahid Sir Point 3)
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideRemarks, setOverrideRemarks] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [overrideSuccessMsg, setOverrideSuccessMsg] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        AdminService.getGlobalTickets(),
        AdminService.getAllCategories(),
      ]);
      if (tRes.success) setTickets(tRes.data);
      if (cRes.success) setCategories(cRes.data);
    } catch (err) { console.error("Sync Error:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const subCategories = useMemo(() => {
    if (!filterCategory) return [];
    return [...new Set(
      tickets.filter(t => t.category_name === filterCategory && t.sub_category_name).map(t => t.sub_category_name)
    )];
  }, [filterCategory, tickets]);

  /* ── FILTERING: uses created_at for date range ── */
  const filteredData = useMemo(() => {
    return tickets.filter(t => {
      const s = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        t.id?.toString().includes(s) || t.ticket_code?.toLowerCase().includes(s) ||
        t.raised_by?.toLowerCase().includes(s) || t.description?.toLowerCase().includes(s) ||
        t.category_name?.toLowerCase().includes(s) || t.sub_category_name?.toLowerCase().includes(s) ||
        t.assigned_worker_name?.toLowerCase().includes(s);
      const matchesTab    = activeTab === "All" || t.status?.toLowerCase() === activeTab.toLowerCase();
      const matchesCat    = !filterCategory || t.category_name === filterCategory;
      const matchesSub    = !filterSubCat   || t.sub_category_name === filterSubCat;
      const matchesRating = !filterRating   || String(t.rating) === filterRating;

      // DATE FILTER: strictly uses created_at (Ticket Raised Date)
      const created     = t.created_at ? new Date(t.created_at) : null;
      const matchesFrom = !filterDateFrom || (created && created >= new Date(filterDateFrom));
      const matchesTo   = !filterDateTo   || (created && created <= new Date(filterDateTo + "T23:59:59"));

      return matchesSearch && matchesTab && matchesCat && matchesSub && matchesRating && matchesFrom && matchesTo;
    });
  }, [tickets, activeTab, searchTerm, filterCategory, filterSubCat, filterRating, filterDateFrom, filterDateTo]);

  const stats = useMemo(() => {
    const rated = tickets.filter(t => t.rating > 0);
    return {
      total:      tickets.length,
      pending:    tickets.filter(t => t.status?.toLowerCase() === "pending").length,
      inProgress: tickets.filter(t => t.status?.toLowerCase() === "work in progress").length,
      resolved:   tickets.filter(t => t.status?.toLowerCase() === "resolved").length,
      rejected:   tickets.filter(t => t.status?.toLowerCase() === "rejected").length,
      hold:       tickets.filter(t => t.status?.toLowerCase() === "hold").length,
      avgRating:  rated.length ? (rated.reduce((a, b) => a + b.rating, 0) / rated.length).toFixed(1) : "—",
    };
  }, [tickets]);

  const activeFilterCount = [filterCategory, filterSubCat, filterDateFrom, filterDateTo, filterRating].filter(Boolean).length;
  const clearFilters = () => {
    setFilterCategory(""); setFilterSubCat(""); setFilterDateFrom("");
    setFilterDateTo(""); setFilterRating(""); setSearchTerm(""); setActiveTab("All");
  };
  const openDrawer = (t) => {
    setSelectedTicket(t);
    setOverrideStatus(t.status || "Pending");
    setOverrideRemarks("");
    setOverrideSuccessMsg(null);
    setDrawerOpen(true);
  };
  const closeDrawer = () => { setDrawerOpen(false); setTimeout(() => setSelectedTicket(null), 280); };

  const handleAdminOverrideSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTicket || !overrideStatus) return;

    if (overrideStatus === selectedTicket.status && !overrideRemarks.trim()) {
      alert("Please select a different status or provide admin remarks.");
      return;
    }

    setUpdatingStatus(true);
    setOverrideSuccessMsg(null);
    try {
      const res = await AdminService.updateTicketStatusByAdmin(
        selectedTicket.id,
        overrideStatus,
        overrideRemarks
      );

      if (res.success) {
        setOverrideSuccessMsg(res.message || "Ticket status updated successfully!");
        
        // Update local selected ticket
        const updated = {
          ...selectedTicket,
          status: overrideStatus,
          remarks: overrideRemarks ? `${selectedTicket.remarks ? selectedTicket.remarks + " | " : ""}[Admin]: ${overrideRemarks}` : selectedTicket.remarks,
          resolved_at: overrideStatus === "Resolved" ? new Date().toISOString() : null,
          rating_status: overrideStatus === "Resolved" ? selectedTicket.rating_status : 0
        };
        setSelectedTicket(updated);

        // Update list
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, ...updated } : t));

        setTimeout(() => setOverrideSuccessMsg(null), 4000);
      }
    } catch (err) {
      alert(err.message || "Failed to update ticket status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) return (
    <div style={S.loaderPage}>
      <style>{CSS_ANIM}</style>
      <div style={S.spinner}/>
      <div style={S.loaderBrand}>ACHAL JEWELS</div>
      <div style={S.loaderSub}>Synchronizing Global Ticket Feed</div>
      <div style={{ display: "flex", gap: "6px", marginTop: "18px" }}>
        {[0,1,2].map(i => <div key={i} style={{ ...S.dot, animationDelay: `${i * 0.2}s` }}/>)}
      </div>
    </div>
  );

  return (
    <>
      <style>{CSS_ANIM + CSS_HOVER}</style>
      <div style={S.layout}>
        <AdminSidebar/>
        <div style={S.mainContent}>

          {/* ══ HEADER ══ */}
          <div style={S.header}>
            <div>
              <div style={S.breadcrumb}>
                <span style={{ color: "#D4AF37", fontWeight: 800 }}>Admin</span>
                <FiChevronRight size={12} style={{ color: "#CBD5E1" }}/>
                <span>Ticket Operations</span>
                <div style={S.viewOnlyTag}><FiShield size={10}/> VIEW ONLY</div>
              </div>
              <h1 style={S.title}>Ticket <span style={{ color: "#D4AF37" }}>Terminal</span></h1>
              <p style={S.subtitle}>Global Ticket monitoring across all departments</p>
            </div>
            <div style={S.headerRight}>
              <button onClick={() => setViewMode(v => v === "table" ? "grid" : "table")} style={S.viewToggleBtn}>
                {viewMode === "table" ? <FiGrid size={14}/> : <FiLayers size={14}/>}
                {viewMode === "table" ? "Grid" : "Table"}
              </button>
              <button onClick={fetchData} className="r-btn" style={S.refreshBtn}>
                <FiRefreshCw size={14}/> Sync Feed
              </button>
            </div>
          </div>

          {/* ══ STAT STRIP ══ */}
          <div style={S.statStrip}>
            <StatCard label="TOTAL"       value={stats.total}      color="#0F172A"/>
            <StatCard label="PENDING"     value={stats.pending}    color="#D97706"/>
            <StatCard label="IN PROGRESS" value={stats.inProgress} color="#2563EB"/>
            <StatCard label="RESOLVED"    value={stats.resolved}   color="#059669"/>
            <StatCard label="REJECTED"    value={stats.rejected}   color="#DC2626"/>
            <StatCard label="ON HOLD"     value={stats.hold}       color="#7C3AED"/>
            <StatCard label="AVG RATING"  value={stats.avgRating}  color="#D4AF37" sub="★ out of 5"/>
          </div>

          {/* ══ STATUS TABS ══ */}
          <div style={S.tabsRow}>
            <div style={S.tabs}>
              {STATUS_TABS.map(tab => {
                const count = tab === "All" ? tickets.length
                  : tickets.filter(t => t.status?.toLowerCase() === tab.toLowerCase()).length;
                return (
                  <button key={tab} onClick={() => setActiveTab(tab)} className="t-btn"
                    style={{
                      ...S.tabBtn,
                      background: activeTab === tab ? "#D4AF37" : "transparent",
                      color:      activeTab === tab ? "#000"    : "#94A3B8",
                      fontWeight: activeTab === tab ? 800       : 600,
                    }}
                  >
                    {tab}
                    <span style={{
                      marginLeft: "7px", fontSize: "10px", fontWeight: 900,
                      background: activeTab === tab ? "rgba(0,0,0,0.12)" : "#E2E8F0",
                      color:      activeTab === tab ? "#000" : "#64748B",
                      padding: "2px 7px", borderRadius: "30px",
                    }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ══ SEARCH + FILTER ROW ══ */}
          <div style={S.searchRow}>
            <div style={S.searchBox}>
              <FiSearch style={S.searchIcon}/>
              <input
                placeholder="Search ticket code, employee, category, description..."
                style={S.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} style={S.clearSearch}><FiX size={12}/></button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(v => !v)}
              style={{
                ...S.filterBtn,
                background:  showFilters || activeFilterCount > 0 ? "#1E293B" : "#FFF",
                color:       showFilters || activeFilterCount > 0 ? "#FFF"    : "#1E293B",
                borderColor: showFilters || activeFilterCount > 0 ? "#1E293B" : "#E2E8F0",
              }}
            >
              <FiFilter size={13}/>
              <span>Filters</span>
              {activeFilterCount > 0 && <span style={S.filterDot}>{activeFilterCount}</span>}
              <FiChevronDown size={12} style={{ transform: showFilters ? "rotate(180deg)" : "none", transition: "0.2s", marginLeft: "2px" }}/>
            </button>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={S.clearAllBtn}><FiX size={11}/> Clear All</button>
            )}

            {/* ── MONTHLY REPORT PDF BUTTON ── */}
            <button
              onClick={() => downloadMonthlyReportPDF(filteredData, filterDateFrom, filterDateTo)}
              className="monthly-report-btn"
              style={S.monthlyReportBtn}
              title="Download category-wise monthly PDF report (filtered by Ticket Raised Date)"
            >
              <FiBarChart2 size={14}/>
              <span>Monthly Report</span>
              <span style={S.monthlyReportBadge}>{filteredData.length}</span>
            </button>

            {/* ── CSV EXPORT BUTTON — NEW ── */}
            <button
              onClick={() => downloadMonthlyReportCSV(filteredData, filterDateFrom, filterDateTo)}
              className="csv-export-btn"
              style={S.csvExportBtn}
              title="Export filtered data as CSV (filtered by Ticket Raised Date)"
            >
              <FiDownload size={14}/>
              <span>CSV Export</span>
            </button>

            <div style={S.resultCount}>
              <span style={{ fontWeight: 800, color: "#0F172A" }}>{filteredData.length}</span>
              <span style={{ color: "#94A3B8" }}> / {tickets.length} records</span>
            </div>
          </div>

          {/* ══ FILTER PANEL ══ */}
          {showFilters && (
            <div style={S.filtersPanel}>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}><FiTag size={10}/> Category</label>
                <div style={S.selectWrap}>
                  <select style={S.filterSelect} value={filterCategory}
                    onChange={e => { setFilterCategory(e.target.value); setFilterSubCat(""); }}>
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <FiChevronDown size={11} style={S.selArrow}/>
                </div>
              </div>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}><FiLayers size={10}/> Sub-Category</label>
                <div style={S.selectWrap}>
                  <select style={{ ...S.filterSelect, opacity: filterCategory ? 1 : 0.5, cursor: filterCategory ? "pointer" : "not-allowed" }}
                    value={filterSubCat} onChange={e => setFilterSubCat(e.target.value)} disabled={!filterCategory}>
                    <option value="">All Sub-Categories</option>
                    {subCategories.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  </select>
                  <FiChevronDown size={11} style={S.selArrow}/>
                </div>
              </div>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}><FiCalendar size={10}/> Date From (Raised)</label>
                <input type="date" style={S.filterSelect} value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}/>
              </div>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}><FiCalendar size={10}/> Date To (Raised)</label>
                <input type="date" style={S.filterSelect} value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}/>
              </div>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}><FiStar size={10}/> Rating</label>
                <div style={S.selectWrap}>
                  <select style={S.filterSelect} value={filterRating} onChange={e => setFilterRating(e.target.value)}>
                    <option value="">Any Rating</option>
                    {[5,4,3,2,1,0].map(r => (
                      <option key={r} value={r}>{r === 0 ? "Not Rated" : `${"★".repeat(r)} (${r} star${r > 1 ? "s" : ""})`}</option>
                    ))}
                  </select>
                  <FiChevronDown size={11} style={S.selArrow}/>
                </div>
              </div>
              {(filterDateFrom || filterDateTo) && (
                <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "2px" }}>
                  <div style={{
                    background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "10px",
                    padding: "8px 12px", fontSize: "11px", color: "#2563EB", fontWeight: 700,
                  }}>
                    <FiCalendar size={10} style={{ marginRight: "5px" }}/>
                    Filtering by <strong>Ticket Raised Date</strong> (created_at)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ TABLE VIEW ══ */}
          {viewMode === "table" && (
            <div style={S.tableWrapper}>
              {filteredData.length === 0 ? (
                <div style={S.noData}>
                  <FiSearch size={34} style={{ color: "#CBD5E1", marginBottom: "10px" }}/>
                  <div style={{ fontWeight: 700, color: "#94A3B8" }}>No tickets match the current filters</div>
                  <div style={{ fontSize: "12px", color: "#CBD5E1", marginTop: "4px" }}>Try clearing filters or adjusting your search</div>
                </div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>TICKET</th>
                      <th style={S.th}>EMPLOYEE</th>
                      <th style={S.th}>CATEGORY</th>
                      <th style={S.th}>WORKER</th>
                      <th style={S.th}>STATUS</th>
                      <th style={S.th}>RATING</th>
                      <th style={S.th}>DATE</th>
                      {/* ── NEW COLUMNS ── */}
                      <th style={{ ...S.th, ...S.thNew }}>RESOLVED ON</th>
                      <th style={{ ...S.th, ...S.thNew }}>TIME TAKEN</th>
                      <th style={{ ...S.th, ...S.thNew }}>FEEDBACK</th>
                      <th style={S.th}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((t, idx) => {
                      const resolvedOnStr = formatResolvedAt(t.resolved_at);
                      const timeTaken    = calcTimeTaken(t.created_at, t.resolved_at);

                      return (
                        <tr key={t.id} className="t-row"
                          style={{ animation: `fadeUp 0.3s ease ${Math.min(idx * 0.03, 0.3)}s both` }}>
                          <td style={S.td}>
                            <div style={S.ticketCode}>{t.ticket_code || `#${t.id}`}</div>
                            <div style={S.ticketIdText}>ID: {t.id}</div>
                          </td>
                          <td style={S.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <Avatar src={t.emp_image} name={t.raised_by} size={38}/>
                              <div>
                                <div style={S.empName}>{t.raised_by || "Unknown"}</div>
                                <div style={S.empPhone}><FiPhone size={9}/> {t.user_phone || "—"}</div>
                              </div>
                            </div>
                          </td>
                          <td style={S.td}>
                            <div style={S.catName}>{t.category_name || "—"}</div>
                            <div style={S.subCatName}>{t.sub_category_name || "—"}</div>
                            <ScannedLocBadge location={t.scanned_location}/>
                          </td>
                          <td style={S.td}>
                            {t.assigned_worker_name
                              ? <div style={S.workerChip}><FiUser size={10}/> {t.assigned_worker_name}</div>
                              : <span style={S.unassigned}>Unassigned</span>}
                          </td>
                          <td style={S.td}><StatusBadge status={t.status}/></td>
                          <td style={S.td}>
                            {t.rating > 0 ? (
                              <div>
                                <div style={{ display: "flex" }}>{RATING_STARS_UI(t.rating)}</div>
                                <div style={{ fontSize: "10px", color: "#94A3B8", marginTop: "2px" }}>{t.rating}/5</div>
                              </div>
                            ) : <span style={S.noRating}>Not rated</span>}
                          </td>
                          <td style={S.td}>
                            <div style={S.dateText}>
                              {new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                            </div>
                            <div style={S.timeText}>
                              {new Date(t.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </td>

                          {/* ── RESOLVED ON: uses resolved_at for display only ── */}
                          <td style={{ ...S.td, ...S.tdNew }}>
                            {resolvedOnStr ? (
                              <div>
                                <div style={S.resolvedDateText}>
                                  {resolvedOnStr.split(",")[0]}
                                </div>
                                <div style={S.resolvedTimeText}>
                                  {resolvedOnStr.includes(",") ? resolvedOnStr.split(",").slice(1).join(",").trim() : ""}
                                </div>
                              </div>
                            ) : (
                              <span style={S.dashText}>—</span>
                            )}
                          </td>

                          {/* ── TIME TAKEN: resolved_at - created_at, display only ── */}
                          <td style={{ ...S.td, ...S.tdNew }}>
                            {timeTaken !== null ? (
                              <div style={S.timeTakenChip}>
                                <FiClock size={10}/>
                                <span>{timeTaken} min</span>
                              </div>
                            ) : (
                              <span style={S.dashText}>—</span>
                            )}
                          </td>

                          {/* ── FEEDBACK ── */}
                          <td style={{ ...S.td, ...S.tdNew, maxWidth: "180px" }}>
                            {t.feedback ? (
                              <div style={S.feedbackCell} title={t.feedback}>
                                <FiMessageSquare size={10} style={{ flexShrink: 0, marginTop: "2px", color: "#94A3B8" }}/>
                                <span style={S.feedbackCellText}>{t.feedback}</span>
                              </div>
                            ) : (
                              <span style={S.dashText}>—</span>
                            )}
                          </td>

                          <td style={S.td}>
                            <div style={{ display: "flex", gap: "6px" }}>
                              <button onClick={() => openDrawer(t)} className="v-btn" style={S.viewBtn}>
                                <FiEye size={12}/> View
                              </button>
                              <button onClick={() => downloadTicketPDF(t)} className="dl-btn" style={S.dlBtn} title="Download PDF">
                                <FiDownload size={12}/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ══ GRID VIEW ══ */}
          {viewMode === "grid" && (
            <div style={S.gridWrapper}>
              {filteredData.length === 0 ? (
                <div style={{ ...S.noData, gridColumn: "1/-1" }}>
                  <FiSearch size={34} style={{ color: "#CBD5E1", marginBottom: "10px" }}/>
                  <div style={{ fontWeight: 700, color: "#94A3B8" }}>No tickets found</div>
                </div>
              ) : filteredData.map((t, idx) => (
                <div key={t.id} className="g-card" onClick={() => openDrawer(t)}
                  style={{ ...S.gridCard, animationDelay: `${Math.min(idx * 0.04, 0.4)}s` }}>
                  <div style={S.gridCardTop}>
                    <div style={S.ticketCode}>{t.ticket_code || `#${t.id}`}</div>
                    <StatusBadge status={t.status}/>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "14px 0" }}>
                    <Avatar src={t.emp_image} name={t.raised_by} size={42}/>
                    <div>
                      <div style={S.empName}>{t.raised_by || "Unknown"}</div>
                      <div style={S.empPhone}><FiPhone size={9}/> {t.user_phone || "—"}</div>
                    </div>
                  </div>
                  <div style={S.gridCatRow}>
                    <FiTag size={10} style={{ color: "#94A3B8" }}/> {t.category_name}
                    {t.sub_category_name && <> &middot; {t.sub_category_name}</>}
                  </div>
                  {t.description && (
                    <div style={S.gridDesc}>{t.description.substring(0, 72)}{t.description.length > 72 ? "..." : ""}</div>
                  )}
                  <ScannedLocBadge location={t.scanned_location}/>
                  {t.resolved_at && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                      <div style={{ ...S.timeTakenChip, fontSize: "10px" }}>
                        <FiClock size={9}/>
                        <span>{calcTimeTaken(t.created_at, t.resolved_at) !== null ? `${calcTimeTaken(t.created_at, t.resolved_at)} min` : "—"}</span>
                      </div>
                    </div>
                  )}
                  <div style={S.gridCardFooter}>
                    <div style={{ display: "flex" }}>
                      {t.rating > 0 ? RATING_STARS_UI(t.rating) : <span style={S.noRating}>Not rated</span>}
                    </div>
                    <button onClick={e => { e.stopPropagation(); downloadTicketPDF(t); }} className="dl-btn" style={S.dlBtn}>
                      <FiDownload size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* ══════════════ DETAIL DRAWER ══════════════ */}
        {selectedTicket && (
          <div style={S.overlay} onClick={closeDrawer}>
            <div
              style={{ ...S.drawer, animation: drawerOpen ? "slideIn 0.3s cubic-bezier(0.4,0,0.2,1) forwards" : "none" }}
              onClick={e => e.stopPropagation()}
            >
              <div style={S.drawerHeader}>
                <div style={{ flex: 1 }}>
                  <div style={S.drawerEyebrow}><FiShield size={11} style={{ color: "#D4AF37" }}/> Audit Reference</div>
                  <h3 style={S.drawerTitle}>{selectedTicket.ticket_code || `Ticket #${selectedTicket.id}`}</h3>
                  <div style={{ marginTop: "8px" }}><StatusBadge status={selectedTicket.status}/></div>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <button onClick={() => downloadTicketPDF(selectedTicket)} style={S.drawerDownloadBtn}>
                    <FiDownload size={14}/> Download
                  </button>
                  <button onClick={closeDrawer} style={S.closeBtn}><FiX size={18}/></button>
                </div>
              </div>

              <div style={S.adminNotice}>
                <FiShield size={13}/>
                <span>Admin Monitoring Mode - Read only.</span>
              </div>

              <div style={S.drawerBody}>
                <Section title="Requestor Profile" icon={<FiUser size={11}/>}>
                  <div style={S.profileBox}>
                    <Avatar src={selectedTicket.emp_image} name={selectedTicket.raised_by} size={62}/>
                    <div style={{ flex: 1 }}>
                      <div style={S.profileName}>{selectedTicket.raised_by || "Unknown Employee"}</div>
                      <div style={S.profilePhone}><FiPhone size={11}/> {selectedTicket.user_phone || "—"}</div>
                    </div>
                    <div style={S.contactActions}>
                      <button onClick={() => window.location.href=`tel:${selectedTicket.user_phone}`} style={S.callBtn}><FiPhone size={14}/></button>
                      <button onClick={() => window.open(`https://wa.me/${selectedTicket.user_phone}`,"_blank")} style={S.waBtn}><FiMessageCircle size={14}/></button>
                    </div>
                  </div>
                </Section>

                <Section title="Ticket Classification" icon={<FiClipboard size={11}/>}>
                  <VerifiedLocationBox location={selectedTicket.scanned_location}/>
                  <DataRow label="Main Category"  value={selectedTicket.category_name}     icon={<FiTag size={11}/>}/>
                  <DataRow label="Sub-Category"   value={selectedTicket.sub_category_name} icon={<FiFileText size={11}/>}/>
                  <DataRow label="Ticket Code"    value={selectedTicket.ticket_code}       icon={<FiHash size={11}/>} mono/>
                  <DataRow label="Description"    value={selectedTicket.description}       icon={<FiInfo size={11}/>} isDesc/>
                  {(selectedTicket.floor_number || selectedTicket.cabin_number) && (
                    <div style={S.locRow}>
                      <FiMapPin size={12}/>
                      Floor {selectedTicket.floor_number || "—"} &mdash; Cabin {selectedTicket.cabin_number || "—"}
                    </div>
                  )}
                </Section>

                <Section title="Evidence Assets" icon={<FiImage size={11}/>}>
                  {selectedTicket.image && (
                    <div style={S.imgPreviewWrap} onClick={() => window.open(`${MEDIA_BASE_URL}${selectedTicket.image}`,"_blank")}>
                      <img src={`${MEDIA_BASE_URL}${selectedTicket.image}`} alt="Evidence" style={S.imgPreview} onError={e => { e.target.style.display="none"; }}/>
                      <div style={S.imgOverlay}><FiExternalLink size={16} style={{ color: "#FFF" }}/> Open Full Size</div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: selectedTicket.image ? "10px" : 0 }}>
                    {selectedTicket.video && <MediaPill type="Video" icon={<FiPlayCircle size={15} color="#EF4444"/>} url={`${MEDIA_BASE_URL}${selectedTicket.video}`}/>}
                    {selectedTicket.audio && <MediaPill type="Audio" icon={<FiMic size={15} color="#3B82F6"/>} url={`${MEDIA_BASE_URL}${selectedTicket.audio}`}/>}
                    {!selectedTicket.image && !selectedTicket.video && !selectedTicket.audio && (
                      <span style={S.noMedia}>No attachments provided</span>
                    )}
                  </div>
                </Section>

                <Section title="Assignment & Workflow" icon={<FiActivity size={11}/>}>
                  <DataRow label="Assigned Worker" value={selectedTicket.assigned_worker_name || "Unassigned"} icon={<FiUser size={11}/>}/>
                  {selectedTicket.remarks ? (
                    <div style={{ marginTop: "6px" }}>
                      <div style={S.remarksLabel}>HOD Remarks</div>
                      <div style={S.remarksBox}>{selectedTicket.remarks}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: "12px", color: "#CBD5E1", fontStyle: "italic", marginTop: "4px" }}>No remarks added by HOD.</div>
                  )}
                </Section>

                {/* Resolution Info in Drawer */}
                <Section title="Resolution Details" icon={<FiCheckCircle size={11}/>}>
                  <DataRow
                    label="Resolved On"
                    value={formatResolvedAt(selectedTicket.resolved_at) || "Not yet resolved"}
                    icon={<FiCalendar size={11}/>}
                  />
                  <DataRow
                    label="Time Taken"
                    value={calcTimeTaken(selectedTicket.created_at, selectedTicket.resolved_at) !== null
                      ? `${calcTimeTaken(selectedTicket.created_at, selectedTicket.resolved_at)} minutes`
                      : "—"}
                    icon={<FiClock size={11}/>}
                  />
                </Section>

                {/* 👑 ADMIN STATUS OVERRIDE SECTION (Shahid Sir Point 3) */}
                <Section title="Update Ticket Status" icon={<FiCheckCircle size={11}/>}>
                  {overrideSuccessMsg && (
                    <div style={{
                      padding: "10px 14px", borderRadius: "10px", background: "#F0FDF4",
                      color: "#059669", border: "1.5px solid #A7F3D0", fontSize: "12px",
                      fontWeight: 700, marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px"
                    }}>
                      <FiCheckCircle size={14} />
                      <span>{overrideSuccessMsg}</span>
                    </div>
                  )}

                  <form onSubmit={handleAdminOverrideSubmit}>
                    <div style={{ marginBottom: "12px" }}>
                      <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" }}>
                        Select Status
                      </label>
                      <select
                        value={overrideStatus}
                        onChange={(e) => setOverrideStatus(e.target.value)}
                        style={{
                          width: "100%", padding: "10px 14px", borderRadius: "10px",
                          border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px",
                          fontWeight: 600, color: "#1E293B", outline: "none", fontFamily: "inherit"
                        }}
                      >
                        <option value="Pending">Pending (Re-open for Review)</option>
                        <option value="In Queue">In Queue (Scheduled)</option>
                        <option value="Work in Progress">Work in Progress (Underway)</option>
                        <option value="Hold">Hold (Paused)</option>
                        <option value="Resolved">Resolved (Completed)</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: "14px" }}>
                      <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" }}>
                        Remarks / Reason (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Reopened by Admin: Pending verification..."
                        value={overrideRemarks}
                        onChange={(e) => setOverrideRemarks(e.target.value)}
                        style={{
                          width: "100%", padding: "10px 14px", borderRadius: "10px",
                          border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px",
                          color: "#1E293B", outline: "none", boxSizing: "border-box", fontFamily: "inherit"
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={updatingStatus}
                      style={{
                        width: "100%", padding: "10px 16px", borderRadius: "10px",
                        border: "none", background: "#1E293B", color: "#FFF",
                        fontSize: "12px", fontWeight: 700, cursor: updatingStatus ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                        transition: "0.2s"
                      }}
                    >
                      <FiCheckCircle size={14} />
                      <span>{updatingStatus ? "Updating..." : "Update Status"}</span>
                    </button>
                  </form>
                </Section>

                {(selectedTicket.rating > 0 || selectedTicket.feedback) && (
                  <Section title="User Feedback" icon={<FiStar size={11}/>}>
                    {selectedTicket.rating > 0 && (
                      <div style={S.ratingRow}>
                        <div style={{ display: "flex", gap: "3px" }}>{RATING_STARS_UI(selectedTicket.rating)}</div>
                        <span style={S.ratingNum}>{selectedTicket.rating} / 5</span>
                      </div>
                    )}
                    {selectedTicket.feedback && (
                      <div style={S.feedbackBox}>
                        <FiMessageSquare size={13} style={{ color: "#94A3B8", flexShrink: 0, marginTop: "2px" }}/>
                        <span>{selectedTicket.feedback}</span>
                      </div>
                    )}
                  </Section>
                )}

                <Section title="Ticket Timeline" icon={<FiClock size={11}/>}>
                  <div style={S.timelineItem}>
                    <div style={S.tlDot}/>
                    <div>
                      <div style={S.tlLabel}>Created</div>
                      <div style={S.tlVal}>
                        {new Date(selectedTicket.created_at).toLocaleString("en-IN", {
                          day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
                        })}
                      </div>
                    </div>
                  </div>
                  {selectedTicket.resolved_at && (
                    <div style={S.timelineItem}>
                      <div style={{ ...S.tlDot, background: "#059669" }}/>
                      <div>
                        <div style={S.tlLabel}>Resolved</div>
                        <div style={S.tlVal}>
                          {new Date(selectedTicket.resolved_at).toLocaleString("en-IN", {
                            day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedTicket.rating_status === 1 && (
                    <div style={S.timelineItem}>
                      <div style={{ ...S.tlDot, background: "#D4AF37" }}/>
                      <div>
                        <div style={S.tlLabel}>Feedback Submitted</div>
                        <div style={S.tlVal}>User has submitted rating &amp; feedback</div>
                      </div>
                    </div>
                  )}
                </Section>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   CSS
───────────────────────────────────────── */
const CSS_ANIM = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { transform:translateX(100%); } to { transform:translateX(0); } }
  @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
`;
const CSS_HOVER = `
  .t-row:hover td     { background: #FAFBFC !important; }
  .t-row .v-btn       { transition: all 0.18s; }
  .t-row:hover .v-btn { background: #D4AF37 !important; color: #000 !important; }
  .dl-btn             { transition: all 0.18s; }
  .dl-btn:hover       { background: #059669 !important; color: #FFF !important; border-color: #059669 !important; }
  .g-card             { transition: transform 0.2s, box-shadow 0.2s; }
  .g-card:hover       { transform: translateY(-3px); box-shadow: 0 20px 40px rgba(0,0,0,0.09) !important; }
  .r-btn              { transition: all 0.2s; }
  .r-btn:hover        { background: #D4AF37 !important; color: #000 !important; border-color: #D4AF37 !important; }
  .t-btn:hover        { color: #1E293B !important; }
  .monthly-report-btn { transition: all 0.2s; }
  .monthly-report-btn:hover { background: #0F172A !important; color: #D4AF37 !important; border-color: #0F172A !important; box-shadow: 0 4px 16px rgba(15,23,42,0.2) !important; }
  .csv-export-btn     { transition: all 0.2s; }
  .csv-export-btn:hover { background: #059669 !important; color: #FFF !important; border-color: #059669 !important; box-shadow: 0 4px 14px rgba(5,150,105,0.22) !important; }
  .table-scroll-wrap::-webkit-scrollbar        { height: 5px; }
  .table-scroll-wrap::-webkit-scrollbar-track  { background: #F1F5F9; border-radius: 99px; }
  .table-scroll-wrap::-webkit-scrollbar-thumb  { background: #CBD5E1; border-radius: 99px; }
  .table-scroll-wrap::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
  .drawer-body-scroll::-webkit-scrollbar       { width: 4px; }
  .drawer-body-scroll::-webkit-scrollbar-track { background: transparent; }
  .drawer-body-scroll::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 99px; }
  html, body { overflow-x: hidden; }
`;

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const S = {
  layout:      { display: "flex", background: "#F0F2F5", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", overflowX: "hidden" },
  mainContent: { marginLeft: "260px", padding: "32px 40px", flex: 1, minWidth: 0, overflowX: "hidden" },

  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "26px" },
  breadcrumb:   { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#94A3B8", fontWeight: 600, marginBottom: "8px" },
  viewOnlyTag:  { display: "inline-flex", alignItems: "center", gap: "5px", background: "#FEF3C7", color: "#D97706", padding: "3px 10px", borderRadius: "30px", fontSize: "10px", fontWeight: 900, marginLeft: "4px" },
  title:        { margin: 0, fontSize: "29px", fontWeight: 900, color: "#0F172A", letterSpacing: "-0.8px" },
  subtitle:     { margin: "4px 0 0", fontSize: "13px", color: "#94A3B8", fontWeight: 500 },
  headerRight:  { display: "flex", gap: "12px", alignItems: "center" },
  viewToggleBtn:{ display: "flex", alignItems: "center", gap: "7px", padding: "11px 18px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },
  refreshBtn:   { display: "flex", alignItems: "center", gap: "8px", padding: "11px 20px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },

  statStrip:     { display: "flex", gap: "12px", marginBottom: "22px", overflowX: "auto", paddingBottom: "4px" },
  statCard:      { background: "#FFF", borderRadius: "16px", padding: "16px 20px", border: "1.5px solid #F1F5F9", flexShrink: 0, minWidth: "100px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" },
  statCardLabel: { display: "block", fontSize: "9px", fontWeight: 800, color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" },
  statCardVal:   { display: "block", fontSize: "26px", fontWeight: 900, marginTop: "4px" },
  statCardSub:   { display: "block", fontSize: "10px", color: "#CBD5E1", fontWeight: 600, marginTop: "2px" },

  tabsRow: { marginBottom: "16px" },
  tabs:    { display: "inline-flex", gap: "4px", background: "#E8ECF0", padding: "5px", borderRadius: "14px", flexWrap: "wrap" },
  tabBtn:  { border: "none", padding: "9px 14px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", transition: "0.2s", display: "flex", alignItems: "center" },

  searchRow:   { display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" },
  searchBox:   { position: "relative", flex: 1, minWidth: "240px", maxWidth: "480px" },
  searchIcon:  { position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8" },
  searchInput: { width: "100%", padding: "12px 38px 12px 42px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", background: "#FFF", fontSize: "13px", fontWeight: 500, color: "#1E293B", transition: "border-color 0.2s", boxSizing: "border-box" },
  clearSearch: { position: "absolute", right: "11px", top: "50%", transform: "translateY(-50%)", background: "#F1F5F9", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
  filterBtn:   { display: "flex", alignItems: "center", gap: "6px", padding: "11px 16px", borderRadius: "12px", border: "1.5px solid", fontWeight: 700, cursor: "pointer", fontSize: "13px", transition: "0.2s", whiteSpace: "nowrap", flexShrink: 0, minWidth: "110px" },
  filterDot:   { background: "#D4AF37", color: "#000", borderRadius: "50%", width: "17px", height: "17px", fontSize: "10px", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  clearAllBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "10px 16px", borderRadius: "12px", border: "1.5px solid #FCA5A5", background: "#FEF2F2", color: "#EF4444", fontWeight: 700, cursor: "pointer", fontSize: "12px", whiteSpace: "nowrap", flexShrink: 0 },
  resultCount: { fontSize: "13px", marginLeft: "auto", whiteSpace: "nowrap" },

  // Monthly Report Button
  monthlyReportBtn: {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "11px 20px", borderRadius: "12px",
    border: "1.5px solid #1E293B", background: "#1E293B",
    color: "#D4AF37", fontWeight: 800, cursor: "pointer",
    fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0,
    boxShadow: "0 2px 10px rgba(15,23,42,0.15)",
  },
  monthlyReportBadge: {
    background: "#D4AF37", color: "#000", borderRadius: "30px",
    padding: "1px 8px", fontSize: "11px", fontWeight: 900,
  },

  // CSV Export Button — NEW
  csvExportBtn: {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "11px 18px", borderRadius: "12px",
    border: "1.5px solid #A7F3D0", background: "#F0FDF4",
    color: "#059669", fontWeight: 800, cursor: "pointer",
    fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0,
  },

  filtersPanel: { background: "#FFF", borderRadius: "18px", padding: "18px 22px", border: "1.5px solid #E2E8F0", marginBottom: "16px", display: "flex", gap: "18px", flexWrap: "wrap", boxShadow: "0 4px 16px rgba(0,0,0,0.04)", animation: "fadeUp 0.2s ease" },
  filterGroup:  { display: "flex", flexDirection: "column", gap: "6px", minWidth: "175px" },
  filterLabel:  { fontSize: "10px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", display: "flex", alignItems: "center", gap: "5px" },
  selectWrap:   { position: "relative" },
  filterSelect: { width: "100%", padding: "9px 30px 9px 12px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: "13px", fontWeight: 600, color: "#1E293B", cursor: "pointer", appearance: "none", transition: "border-color 0.2s", outline: "none" },
  selArrow:     { position: "absolute", right: "9px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", pointerEvents: "none" },

  tableWrapper: { background: "#FFF", borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1.5px solid #F1F5F9", overflowX: "auto", overflowY: "hidden" },
  table:        { width: "100%", borderCollapse: "separate", borderSpacing: "0 2px", minWidth: "1200px" },
  th:           { padding: "13px 16px", textAlign: "left", fontSize: "10px", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 800, whiteSpace: "nowrap", background: "#FFF" },
  thNew:        { color: "#2563EB", background: "#EFF6FF" },
  td:           { padding: "15px 16px", verticalAlign: "middle", borderBottom: "1.5px solid #F8FAFC" },
  tdNew:        { background: "#FAFCFF" },

  ticketCode:   { fontFamily: "'DM Mono', monospace", fontSize: "12px", fontWeight: 600, background: "#D4AF3718", color: "#9A7B10", padding: "5px 10px", borderRadius: "8px", display: "inline-block" },
  ticketIdText: { fontSize: "10px", color: "#CBD5E1", marginTop: "4px", fontFamily: "'DM Mono', monospace" },
  empName:      { fontWeight: 800, color: "#0F172A", fontSize: "14px" },
  empPhone:     { fontSize: "11px", color: "#94A3B8", display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" },
  catName:      { fontWeight: 700, color: "#334155", fontSize: "13px" },
  subCatName:   { fontSize: "11px", color: "#94A3B8", marginTop: "2px" },
  workerChip:   { display: "inline-flex", alignItems: "center", gap: "5px", background: "#EFF6FF", color: "#2563EB", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", fontWeight: 700 },
  unassigned:   { fontSize: "12px", color: "#CBD5E1", fontStyle: "italic" },
  noRating:     { fontSize: "12px", color: "#CBD5E1" },
  dateText:     { fontSize: "13px", fontWeight: 700, color: "#334155" },
  timeText:     { fontSize: "11px", color: "#94A3B8", marginTop: "2px" },
  viewBtn:      { display: "inline-flex", alignItems: "center", gap: "6px", background: "#1E293B", color: "#FFF", border: "none", padding: "9px 14px", borderRadius: "10px", cursor: "pointer", fontWeight: 700, fontSize: "12px", whiteSpace: "nowrap" },
  dlBtn:        { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#F0FDF4", color: "#059669", border: "1.5px solid #A7F3D0", padding: "9px 10px", borderRadius: "10px", cursor: "pointer", fontWeight: 700, flexShrink: 0 },
  noData:       { padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },

  resolvedDateText: { fontSize: "12px", fontWeight: 700, color: "#059669" },
  resolvedTimeText: { fontSize: "10px", color: "#6EE7B7", marginTop: "2px" },
  timeTakenChip:    { display: "inline-flex", alignItems: "center", gap: "5px", background: "#F5F3FF", color: "#7C3AED", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", fontWeight: 800 },
  feedbackCell:     { display: "flex", gap: "6px", alignItems: "flex-start", fontSize: "11px", color: "#475569", fontStyle: "italic", lineHeight: "1.5" },
  feedbackCellText: { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" },
  dashText:         { fontSize: "14px", color: "#CBD5E1", fontWeight: 700 },

  gridWrapper:    { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(275px, 1fr))", gap: "16px" },
  gridCard:       { background: "#FFF", borderRadius: "20px", padding: "20px", border: "1.5px solid #F1F5F9", cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", animation: "fadeUp 0.3s ease both" },
  gridCardTop:    { display: "flex", justifyContent: "space-between", alignItems: "center" },
  gridCatRow:     { display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#64748B", fontWeight: 600, marginBottom: "8px" },
  gridDesc:       { fontSize: "12px", color: "#94A3B8", lineHeight: "1.6", marginBottom: "8px" },
  gridCardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "12px", borderTop: "1.5px solid #F8FAFC" },

  overlay:      { position: "fixed", inset: 0, background: "rgba(10,15,30,0.44)", zIndex: 1000, display: "flex", justifyContent: "flex-end", backdropFilter: "blur(8px)" },
  drawer:       { width: "530px", maxWidth: "95vw", height: "100%", background: "#F8FAFC", boxShadow: "-30px 0 80px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" },
  drawerHeader: { padding: "26px 28px 20px", borderBottom: "1.5px solid #E8EDF2", display: "flex", gap: "16px", alignItems: "flex-start", background: "#FFF" },
  drawerEyebrow:{ display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "#D4AF37", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "6px" },
  drawerTitle:  { margin: 0, fontSize: "22px", fontWeight: 900, color: "#0F172A" },
  closeBtn:     { background: "#F1F5F9", border: "none", width: "42px", height: "42px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", flexShrink: 0 },
  drawerDownloadBtn: { display: "flex", alignItems: "center", gap: "7px", padding: "10px 16px", borderRadius: "12px", border: "1.5px solid #A7F3D0", background: "#F0FDF4", color: "#059669", fontWeight: 800, cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 },
  adminNotice:  { display: "flex", alignItems: "center", gap: "10px", background: "#FFF7ED", borderBottom: "1px solid #FDE68A", padding: "10px 28px", fontSize: "12px", color: "#92400E", fontWeight: 600 },
  drawerBody:   { flex: 1, overflowY: "auto", padding: "22px 28px" },

  secLabel: { fontSize: "10px", color: "#94A3B8", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: "10px", display: "flex", alignItems: "center" },
  secCard:  { background: "#FFF", padding: "18px 20px", borderRadius: "18px", border: "1.5px solid #F1F5F9", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" },

  profileBox:     { display: "flex", alignItems: "center", gap: "14px" },
  profileName:    { fontWeight: 800, fontSize: "17px", color: "#0F172A" },
  profilePhone:   { display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "#94A3B8", marginTop: "3px" },
  contactActions: { display: "flex", gap: "10px", marginLeft: "auto" },
  callBtn:        { background: "#ECFDF5", color: "#059669", border: "none", padding: "12px", borderRadius: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  waBtn:          { background: "#F0FDF4", color: "#16A34A", border: "none", padding: "12px", borderRadius: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },

  locRow: { display: "flex", alignItems: "center", gap: "8px", background: "#F8FAFC", padding: "10px 14px", borderRadius: "10px", marginTop: "10px", fontSize: "13px", fontWeight: 600, color: "#475569" },

  imgPreviewWrap: { position: "relative", borderRadius: "14px", overflow: "hidden", cursor: "pointer", border: "1.5px solid #E2E8F0" },
  imgPreview:     { width: "100%", maxHeight: "210px", objectFit: "cover", display: "block" },
  imgOverlay:     { position: "absolute", inset: 0, background: "rgba(0,0,0,0.38)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: 0, transition: "0.2s", color: "#FFF", fontWeight: 700, fontSize: "13px" },

  mediaPill: { display: "inline-flex", alignItems: "center", gap: "8px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", padding: "10px 16px", borderRadius: "12px", cursor: "pointer", transition: "0.2s" },
  noMedia:   { fontSize: "12px", color: "#CBD5E1", fontStyle: "italic" },

  remarksLabel: { fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "8px" },
  remarksBox:   { background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: "12px", padding: "12px 14px", fontSize: "13px", lineHeight: "1.7", color: "#334155" },

  ratingRow:   { display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" },
  ratingNum:   { fontSize: "14px", fontWeight: 900, color: "#F59E0B" },
  feedbackBox: { display: "flex", gap: "10px", background: "#FFFBEB", border: "1.5px solid #FDE68A", borderRadius: "12px", padding: "12px 14px", fontSize: "13px", color: "#78350F", lineHeight: "1.6" },

  timelineItem: { display: "flex", gap: "14px", alignItems: "flex-start", marginBottom: "14px" },
  tlDot:        { width: "10px", height: "10px", borderRadius: "50%", background: "#D4AF37", flexShrink: 0, marginTop: "4px" },
  tlLabel:      { fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase" },
  tlVal:        { fontSize: "13px", fontWeight: 600, color: "#334155", marginTop: "3px" },

  loaderPage:  { height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "#F0F2F5" },
  spinner:     { width: "46px", height: "46px", border: "5px solid #E2E8F0", borderTopColor: "#D4AF37", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loaderBrand: { marginTop: "22px", fontSize: "17px", fontWeight: 900, color: "#1E293B", letterSpacing: "5px" },
  loaderSub:   { color: "#94A3B8", fontSize: "12px", marginTop: "5px" },
  dot:         { width: "7px", height: "7px", borderRadius: "50%", background: "#D4AF37", animation: "pulse 1.2s ease infinite" },
};

export default AdminTickets;